import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Bold, Italic, Link2, List, ListOrdered } from "lucide-react";

import { cn } from "@/lib/utils/cn";

// The partners invite window renders this editor too, so it registers its own
// strings rather than relying on the action-plans slice having loaded.
import "../i18n";

/**
 * Minimal dependency-free rich-text editor (contentEditable + `execCommand`)
 * for the consultation email body — bold / italic / bullet & numbered lists /
 * link, matching the old app's editor without pulling in a heavy library (which
 * the artifact/SPA CSP + bundle budget discourage). Output HTML is sanitised
 * server-side (bleach) on save/send, so the loose contentEditable markup is safe.
 *
 * The editor is uncontrolled after mount (we seed innerHTML once) to avoid caret
 * jumps; every input reports the current HTML back through `onChange`.
 */
export function RichTextEditor({
  value,
  onChange,
  ariaLabel,
}: {
  value: string;
  onChange: (html: string) => void;
  ariaLabel?: string;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);

  // Seed the initial HTML once; afterwards the DOM is the source of truth.
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== value) {
      ref.current.innerHTML = value;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exec = (command: string, arg?: string) => {
    document.execCommand(command, false, arg);
    ref.current?.focus();
    if (ref.current) onChange(ref.current.innerHTML);
  };

  const addLink = () => {
    const url = window.prompt(t("actionPlans.richText.linkPrompt"));
    if (url) exec("createLink", url);
  };

  const btn =
    "grid size-8 place-items-center rounded text-muted-foreground hover:bg-accent hover:text-foreground [&_svg]:size-4";

  return (
    <div className="rounded-md border border-input">
      <div className="flex items-center gap-0.5 border-b border-input p-1">
        <button type="button" className={btn} title={t("actionPlans.richText.bold")} onClick={() => exec("bold")}>
          <Bold aria-hidden />
        </button>
        <button type="button" className={btn} title={t("actionPlans.richText.italic")} onClick={() => exec("italic")}>
          <Italic aria-hidden />
        </button>
        <button
          type="button"
          className={btn}
          title={t("actionPlans.richText.bulletList")}
          onClick={() => exec("insertUnorderedList")}
        >
          <List aria-hidden />
        </button>
        <button
          type="button"
          className={btn}
          title={t("actionPlans.richText.numberedList")}
          onClick={() => exec("insertOrderedList")}
        >
          <ListOrdered aria-hidden />
        </button>
        <button type="button" className={btn} title={t("actionPlans.richText.link")} onClick={addLink}>
          <Link2 aria-hidden />
        </button>
      </div>
      <div
        ref={ref}
        role="textbox"
        aria-multiline="true"
        aria-label={ariaLabel}
        contentEditable
        onInput={(e) => onChange(e.currentTarget.innerHTML)}
        className={cn(
          "min-h-32 max-h-80 overflow-y-auto px-3 py-2 text-sm outline-none",
          "[&_a]:text-primary [&_a]:underline [&_ul]:list-disc [&_ol]:list-decimal",
          "[&_ul]:pl-5 [&_ol]:pl-5",
        )}
      />
    </div>
  );
}
